import { Draft } from 'immer';
import {
  assertProjectionContinuity,
  assertProjectionIdentities,
} from './assertProjectionIdentities';
import { clientAgeForPolicyYear } from './clientAgeCalculation';
import { ltcBeginsLabel } from './deriveMilestones';
import { getMonthPayout } from './projectionTotals';
import { computeMonthlyRoomAndBoardCost } from './sharedEligibility';
import { coverRoomAndBoardWithIndemnitySurplus } from './sharedSurplus';
import { PolicyProjectionStrategy } from './strategyTypes';

// Supplied only for joint policies. The projection then runs over the merged,
// claimant-tagged stream, resolving each row's claimant client + phase plan and
// threading the shared policy-year anchor into the strategy. The session client
// (passed as `mutableClient`) serves 'client' rows; `partnerClient` serves
// 'partner' rows. Built by `buildJointMonthlyCareInputs` + the joint orchestration.
export interface JointProjectionContext {
  monthlyCareInputs: MonthlyCareInput[];
  partnerClient: Draft<Client>;
  // Integer policy year at monthIndex 0 — i.e. Jan 1 of the earliest spouse's
  // onset year (the calendar origin `buildJointMonthlyCareInputs` measures every
  // spouse's offset from). `resolveRowPolicyYear` adds `floor(monthIndex/12)` so
  // each spouse lands on its true calendar policy year by integer math.
  anchorPolicyYear: number;
}

export interface ProcessClaimProjectionOptions {
  // If true, every month is checked against assertProjectionIdentities (and
  // the whole stream against assertProjectionContinuity) and a failure
  // throws. Defaults to true — strategies are bugs if identities fail, and we
  // want them to surface loudly during shadow mode.
  assertIdentities?: boolean;
  // Present only for joint policies — see JointProjectionContext.
  joint?: JointProjectionContext;
}

export interface ProcessClaimProjectionResult<TState = unknown, TDeltas = unknown> {
  projection: ClaimProjection<TState, TDeltas>;
  finalDeathBenefit: number;
  closingState: MonthlyState<TState>;
}

export function processClaimProjection<TState, TDeltas>(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  strategy: PolicyProjectionStrategy<TState, TDeltas>,
  options: ProcessClaimProjectionOptions = {},
): ProcessClaimProjectionResult<TState, TDeltas> {
  const { assertIdentities = true, joint } = options;

  // Single-life draws the stream + plans from the session client; joint draws
  // the merged stream from the context and resolves plans per claimant.
  const monthlyCareInputs = joint
    ? joint.monthlyCareInputs
    : mutableClient.projectionInputs.monthlyCareInputs;

  const clientPhaseLookup = new Map(
    mutableClient.projectionInputs.phaseCarePlans.map(plan => [plan.carePhase, plan]),
  );
  const partnerPhaseLookup = joint
    ? new Map(
        joint.partnerClient.projectionInputs.phaseCarePlans.map(plan => [plan.carePhase, plan]),
      )
    : null;

  const initialState = strategy.initClaimState(mutableClient, ltcPolicy, joint?.anchorPolicyYear);
  const terms = strategy.buildClaimTerms(mutableClient, ltcPolicy, joint?.anchorPolicyYear);

  const yearSnapshots: YearSnapshot[] = [];
  const yearSnapshotIndexByPolicyYear = new Map<number, number>();

  let openState = initialState;
  const months: MonthlyEntry<TState, TDeltas>[] = [];
  // Last emitted row per claimant ('client' / 'partner', or '__single__' for
  // single-life). Joint streams interleave the spouses when their care overlaps,
  // so the global previous row is often the OTHER spouse — milestone transition
  // detection needs the prior row of the SAME claimant instead.
  const lastMonthByClaimant = new Map<string, MonthlyEntry<TState, TDeltas>>();

  for (const monthlyCareInput of monthlyCareInputs) {
    const isPartnerRow = monthlyCareInput.claimant === 'partner';
    const rowClient = isPartnerRow && joint ? joint.partnerClient : mutableClient;
    const phaseLookup = isPartnerRow && partnerPhaseLookup ? partnerPhaseLookup : clientPhaseLookup;
    const phaseCarePlan = phaseLookup.get(monthlyCareInput.carePhase);
    if (!phaseCarePlan) continue;

    const claimantKey = monthlyCareInput.claimant ?? '__single__';
    const priorMonth = months.length > 0 ? months[months.length - 1] : null;
    const priorClaimantMonth = lastMonthByClaimant.get(claimantKey) ?? null;
    const month = strategy.computeMonth({
      client: rowClient,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
      jointAnchorPolicyYear: joint?.anchorPolicyYear,
    });

    // Client age is a projection-level fact: it depends only on the policy-year
    // clock and the client's birth year, not on anything strategy-specific.
    // Normalize it here so every strategy — and the accumulation timeline below
    // — reports the same age for a given policy year, matching the care-timeline
    // UI's birth-year basis. (Strategies still seed a provisional age via
    // `calculateClientAge`; this is the source of truth.)
    month.context.clientAge = clientAgeForPolicyYear(
      rowClient.intakeSurvey?.clientBirthYear,
      month.context.policyYear,
      terms.policyPremiumStartYear,
    );

    // `deriveStandardMilestones` baked the `ltc-begins` label off the provisional
    // age strategies seed, which can drift years on in-force policies. Re-label it
    // off the authoritative normalized age above. Only the first emitted month per
    // claimant carries this milestone, so this targeted patch is cheap.
    const ltcBegins = month.events.milestones.find(m => m.kind === 'ltc-begins');
    if (ltcBegins) {
      ltcBegins.label = ltcBeginsLabel(month.context.clientAge);
    }

    // Room & board is out-of-pocket for Independent Living (home-style care is
    // priced and covered separately by the strategy). Injected here — a
    // projection-level fact, like clientAge above — so every strategy stays
    // room-&-board-agnostic; zero for all non-IL phases.
    month.facts.roomAndBoardCost = computeMonthlyRoomAndBoardCost(
      rowClient,
      phaseCarePlan,
      monthlyCareInput.monthsIntoPhase,
    );

    // Indemnity is fungible cash, so this month's benefit above the eligible
    // care cost covers out-of-pocket room & board before banking as surplus.
    // Must run after the R&B injection above and before both the identity
    // assert (it reduces the accrued-surplus delta + close reserve together, so
    // the reserve identity holds) and `openState = month.state` below (so next
    // month opens on the reduced reserve). No-op for reimbursement/non-IL rows.
    coverRoomAndBoardWithIndemnitySurplus(month, terms.isIndemnity);

    if (assertIdentities) {
      assertProjectionIdentities(month);
    }

    // Snapshot the policy year on first sight. For joint, snapshots (and the
    // claim terms above) are policy-level metadata — rates, caps, pool sizes —
    // so they're intentionally sourced from the session client; per-claimant
    // facts come from `month` instead.
    const { policyYear, claimYearOffset } = month.context;
    if (!yearSnapshotIndexByPolicyYear.has(policyYear)) {
      // In-claim snapshots carry the claim-relative year offset; the
      // accumulation timeline below attaches `yearsFromAnchor` instead.
      const snapshot: YearSnapshot = {
        ...strategy.buildYearSnapshot({
          client: mutableClient,
          ltcPolicy,
          policyYear,
        }),
        claimYearOffset,
      };
      yearSnapshotIndexByPolicyYear.set(policyYear, yearSnapshots.length);
      yearSnapshots.push(snapshot);
    }

    months.push(month);
    lastMonthByClaimant.set(claimantKey, month);
    openState = month.state;
  }

  // Cross-row open/close continuity (P2-41): every row's openState must be
  // the prior close state (per claimant for the waiting period on joint
  // streams; across the merged stream for the shared pools/strategyState).
  if (assertIdentities) {
    assertProjectionContinuity(months);
  }

  // Mark each claimant's "Payout begins" boundary on the first month that
  // actually pays a positive benefit (per claimant — joint streams interleave
  // the spouses, who reach their own first payout independently). This is NOT
  // the 2-ADL gate-clear month: the waiting period can still be running then, so
  // the gate-clear month frequently pays $0. Keying the milestone off the first
  // positive payout keeps it honest and lets the premium cutoff that reads it
  // stop premiums exactly when benefits begin (premiums run through the waiting
  // period). Nothing is emitted for a claimant that never pays.
  const claimStartMarkedClaimants = new Set<string>();
  for (const month of months) {
    const claimantKey = month.context.claimant ?? '__single__';
    if (claimStartMarkedClaimants.has(claimantKey)) continue;
    if (getMonthPayout(month) <= 0) continue;
    claimStartMarkedClaimants.add(claimantKey);
    month.events.milestones.push({ kind: 'claim-start', label: 'Payout begins' });
  }

  // Months from policy issue to the FIRST paying month anywhere in the stream —
  // the premium-paying / out-of-pocket window. Anchored at the start of the issue
  // year on the projection's integer-year clock (the same `policyYear` the rest
  // of the projection uses), so the premium schedule and this anchor reconcile by
  // construction: `(policyYear − 1) × 12` counts whole years from issue to the
  // payout policy year, and `monthIndex % 12` adds the out-of-pocket months
  // within it. Absent when nothing ever pays — premiums then fall back to stopping
  // at care onset. Joint streams interleave the spouses; the first paying row in
  // calendar order is the earliest benefit across both, which is exactly when the
  // shared premium stream stops. Spread into the returned terms rather than
  // mutated onto the strategy's object (which a caller may reuse).
  const firstPayingMonth = months.find(month => getMonthPayout(month) > 0);
  const firstPayoutTerms: Partial<ClaimTerms> = firstPayingMonth
    ? {
        monthsFromIssueToFirstPayout:
          (firstPayingMonth.context.policyYear - 1) * 12 +
          (firstPayingMonth.context.monthIndex % 12),
        firstPayoutPolicyYear: firstPayingMonth.context.policyYear,
        firstPayoutCalendarYear: firstPayingMonth.facts.projectedCareYear,
      }
    : {};

  // Mark the start of each claimant's out-of-pocket period with a single
  // boundary milestone. The first non-paying row (per claimant — joint streams
  // interleave the spouses, who serve their own gates/EP independently) gets an
  // "Out-of-pocket period begins" badge so the unified monthly-breakdown table
  // surfaces the OOP→benefits boundary that the old split-table timeline showed.
  // Nothing is emitted when a projection starts paying immediately.
  const oopMarkedClaimants = new Set<string>();
  for (const month of months) {
    if (month.events.ineligibilityReason == null) continue;
    const claimantKey = month.context.claimant ?? '__single__';
    if (oopMarkedClaimants.has(claimantKey)) continue;
    oopMarkedClaimants.add(claimantKey);
    month.events.milestones.push({
      kind: 'out-of-pocket',
      label: 'Out-of-pocket period begins',
    });
  }

  const finalDeathBenefit = strategy.finalizeDeathBenefit({
    ltcPolicy,
    closingState: openState,
  });

  const accumulationTimeline = buildAccumulationTimeline(
    mutableClient,
    ltcPolicy,
    strategy,
    terms,
    months,
  );

  return {
    projection: {
      terms: { ...terms, ...firstPayoutTerms },
      yearSnapshots,
      accumulationTimeline,
      months,
    },
    finalDeathBenefit,
    closingState: openState,
  };
}

// Pre-care growth timeline: one row per policy year from issue (year 1) up to
// and including the policy year LTC care begins. We reuse each strategy's
// `buildYearSnapshot` — which already returns a given policy year's inflated
// pools/caps/face/value for any year — so no per-strategy growth math is
// duplicated. Returns [] when care begins in policy year 1 (no accumulation
// phase) or on a pre-policy claim (policy year < 1 at month 0).
function buildAccumulationTimeline<TState, TDeltas>(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  strategy: PolicyProjectionStrategy<TState, TDeltas>,
  terms: ClaimTerms,
  months: MonthlyEntry<TState, TDeltas>[],
): AccumulationYear[] {
  const claimStartPolicyYear = months[0]?.context.policyYear ?? 0;
  if (claimStartPolicyYear < 2) return [];

  const timeline: AccumulationYear[] = [];
  for (let policyYear = 1; policyYear <= claimStartPolicyYear; policyYear += 1) {
    const calendarYear = terms.policyPremiumStartYear + (policyYear - 1);
    // Accumulation rows are anchor-relative (offset from the cost-inflation
    // "today"), unlike the in-claim snapshots' claim-relative clock.
    const snapshot: YearSnapshot = {
      ...strategy.buildYearSnapshot({
        client: mutableClient,
        ltcPolicy,
        policyYear,
      }),
      yearsFromAnchor: calendarYear - terms.projectionAnchorYear,
    };
    timeline.push({
      policyYear,
      calendarYear,
      clientAge: clientAgeForPolicyYear(
        mutableClient.intakeSurvey?.clientBirthYear,
        policyYear,
        terms.policyPremiumStartYear,
      ),
      snapshot,
      isCareStartYear: policyYear === claimStartPolicyYear,
    });
  }
  return timeline;
}

